<script setup lang="ts">
import { computed, type Component, type VNodeChild} from 'vue';

import { BrandLoader } from '@/components/global';

interface Column {
  label: string;
  key: string;
}

interface Identifiable {
  id?: number | string;
  [key: string]: any;
}

interface TableProps<T extends Identifiable> {
  columns: Column[];
  data?: T[];
  loadingData?: boolean;
  emptyText?: string | Component | VNodeChild;
  isResultNotFound?: boolean;
  zoom?: boolean;
  clickable?: boolean;
}

const props = withDefaults(defineProps<TableProps<any>>(), {
  loadingData: false,
  emptyText: 'No data available',
  zoom: false,
  isResultNotFound: false,
  clickable: true,
});

const emit = defineEmits<(e: 'clickRow', rowData: any) => void>();

const emptyTextFallback = computed(() => props.isResultNotFound ? 'No result found' : 'No data available');

const handleOnClick = (rowData: any) => {
  props.clickable && emit('clickRow', rowData);
}
</script>

<template>
  <div class="w-100 overflow-auto" style="height: 50vh;">
    <table :style="{ width: '100%', height: data && data.length > 0 ? 'auto' : '100%' }">
      <thead>
        <tr>
          <th
            v-for="col in columns"
            :key="col.key"
            class="text-nowrap"
            :style="zoom ? { fontSize: 'var(--font-xs)' } : { fontSize: 'var(--font-sm)' }"
          >
            {{ col.label }}
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-if="loadingData">
          <td :colspan="columns.length" class="py-1 text-center">
            <slot name="loading">
              <BrandLoader />
            </slot>
          </td>
        </tr>

        <template v-else-if="data && data.length > 0">
          <tr
            v-for="(row, rowIndex) in data"
            :key="row.id ?? rowIndex"
            tabindex="0"
            @click="handleOnClick(row)"
            @keydown.enter="handleOnClick(row)"
            :style="{ fontSize: zoom ? 'var(--font-sm)' :  'var(--font-base)' }"
            :class="['table-list-row', clickable ? 'table-cell-clickable' : '']"
          >
            <td
              v-for="col in columns"
              :key="col.key"
              class="table-cell text-start text-nowrap"
            >
              <slot
                name="renderCell"
                :rowIndex="rowIndex"
                :columnKey="col.key"
                :rowData="row"
              >
                <!-- Default cell rendering -->
                {{ row[col.key] ?? '-' }}
              </slot>
            </td>
          </tr>
        </template>

        <tr v-else>
          <td :colspan="columns.length" class="py-1 text-center">
            <template v-if="$slots.emptyText">
              <slot name="emptyText" />
            </template>
            <template v-else>
              <span v-if="typeof emptyText === 'string'">
                {{ emptyText ?? emptyTextFallback }}
              </span>
              <component v-else :is="emptyText" />
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
table,
thead tr th,
tbody tr td {
  border: none;
  border-collapse: separate;
  border-spacing: 0 0.625rem;
}

thead tr th {
  padding: 1rem 1.25rem;
  color: #00000080;
  font-weight: 400;
  line-height: 100%;
  text-transform: uppercase;
}

tbody tr.table-list-row {
  max-height: 3.125rem;
  background-color: var(--ch-table-row);
  color: #000000CC;
  transition: all ease-in-out 0.3s;
}

tbody tr td.table-cell {
  padding: 0.25rem 1.25rem;
  height: 3.125rem;
  font-weight: 400;
  line-height: 100%;
  vertical-align: middle;
}

.table-container .table-cell-clickable:hover,
.table-container .table-cell-clickable:focus {
  background-color: var(--ch-blue-100);
  color: #FFFFFFCC;
  cursor: pointer;
  outline: none;
}

tr th:first-child, tr td:first-child {
  border-top-left-radius: var(--font-xxs);
  border-bottom-left-radius: var(--font-xxs);
}

tr th:last-child, tr td:last-child {
  border-top-right-radius: var(--font-xxs);
  border-bottom-right-radius: var(--font-xxs);
}
</style>